import { cva } from 'class-variance-authority';

import { cn } from '@heidi/utils';

import { focusRingInvalidExperimental, focusRingSolid } from '../styles/focus-ring';
import { typographyVariant } from '../typography';
import type { FlushableVariant } from './button.types';

/** Variants that support the flush prop — keep in sync with FlushableVariant type. */
const FLUSHABLE_VARIANTS: FlushableVariant[] = [
  'ghost',
  'panel_ghost',
  'destructive_ghost',
  'success_ghost',
];

export const variantClassesExperimental = {
  default: cn(
    'bg-background-button-primary text-text-primary-invert hover:bg-background-button-primary-hover active:bg-background-button-primary-pressed'
  ),
  secondary: cn(
    'bg-background-secondary text-text-primary hover:bg-background-secondary-hover active:bg-background-secondary-pressed',
    // Keeps the trigger visually "pressed" while a popover/menu is open.
    'aria-expanded:bg-background-secondary aria-expanded:text-text-primary'
  ),
  outline: cn(
    'border-outline bg-transparent text-text-primary hover:bg-background-tertiary-hover hover:text-text-primary active:bg-background-tertiary-pressed',
    'aria-expanded:bg-background-tertiary-hover aria-expanded:text-text-primary'
  ),
  // @deprecated Use `outline` instead.
  surface: cn(
    'border-outline bg-surface text-text-primary hover:bg-surface/50 hover:text-text-primary active:bg-surface/40',
    // newBrandDark: `bg-surface` is the near-black elevated surface; use the page
    // bg so the button reads as a neutral filled control against the dark page.
    'newBrandDark:bg-surface-page newBrandDark:hover:bg-surface-page/50 newBrandDark:active:bg-surface-page/40',
    // Keeps the trigger visually "pressed" while a popover/menu is open.
    'aria-expanded:bg-surface/50 aria-expanded:text-text-primary newBrandDark:aria-expanded:bg-surface-page/50'
  ),
  ghost: cn(
    'hover:bg-background-tertiary-hover hover:text-text-primary active:bg-background-tertiary-pressed',
    'aria-expanded:bg-background-tertiary-hover aria-expanded:text-text-primary'
  ),
  // @deprecated Use `ghost` instead. Removed once
  // `release-scribe-enable-new-session-list` is at 100% and
  // `IS_SIDEBAR_PANEL_ENABLED` is retired
  panel_ghost: cn(
    'bg-transparent text-text-primary',
    'hover:bg-background-secondary hover:text-text-primary',
    'active:bg-background-secondary-pressed',
    'data-[active=true]:bg-background-secondary data-[active=true]:text-text-primary',
    'data-[active=true]:hover:bg-background-secondary-hover',
    'data-[active=true]:active:bg-background-secondary-pressed'
  ),
  // Success-tone colours are the `-forms` baseline (legacy `validation-*`). Their
  // DS-1202 reworked `semantic-*` / `button-background-*` colours are applied as an
  // override (see `buttonToneColourOverridesExperimental`) only when the
  // `release-experimental-design-system-buttons` flag is on. Keep the two in sync.
  success: cn(
    'bg-validation-success text-text-primary-invert hover:bg-validation-success-hover active:bg-validation-success-pressed'
  ),
  success_ghost: cn(
    'text-validation-success hover:bg-validation-success-muted active:bg-validation-success-muted active:text-validation-success-pressed'
  ),
  // Destructive-tone colours ship the DS-1202 rework on the `-forms` flag itself
  // (DS-1567) — they are no longer held back behind the separate `-buttons` flag,
  // so there is no override entry for them.
  destructive_ghost: cn(
    'text-semantic-text-destructive hover:bg-semantic-background-destructive-hover active:bg-semantic-background-destructive-active active:text-semantic-text-destructive-active'
  ),
  destructive: cn(
    'bg-button-background-destructive text-text-primary-invert hover:bg-button-background-destructive-hover active:bg-button-background-destructive-active'
  ),
  destructive_soft: cn(
    'bg-semantic-background-destructive text-semantic-text-destructive hover:bg-semantic-background-destructive-active active:bg-semantic-background-destructive-active active:text-semantic-text-destructive-active'
  ),
  // @deprecated Use `destructive_soft` for a destructive action; for an
  // error-state field border, use the control's invalid state instead.
  destructive_outline_with_border: cn(
    'border-semantic-text-destructive bg-surface text-semantic-text-destructive hover:bg-semantic-background-destructive-hover active:bg-semantic-background-destructive-active active:text-semantic-text-destructive-active'
  ),
  link: cn(
    'text-text-link underline-offset-4 hover:text-text-link-hover hover:underline hover:shadow-none active:text-text-link-pressed'
  ),
  // White by default; the dark tier override flips it to black so it reads on the
  // light fill.
  pro: cn(
    'bg-tier-pro text-text-primary-on-dark hover:bg-tier-pro-hover [.skiesTierDark_&]:text-text-primary-invert'
  ),
  pro_plus: cn(
    'bg-tier-practice text-text-primary-on-dark hover:bg-tier-practice-hover [.skiesTierDark_&]:text-text-primary-invert'
  ),
} as const;

/**
 * The remainder of the DS-1202 button-colour rework, gated behind the
 * `release-experimental-design-system-buttons` flag (separate from the
 * `-forms` flag that gates the button's structural experimental styles).
 *
 * **Success tone only.** The destructive tone shipped its reworked colours on
 * the `-forms` flag itself (DS-1567) and now lives directly in
 * `variantClassesExperimental`; the success tone stays behind this flag so it
 * can land bundled with the next round of button reworks.
 *
 * Each entry fully replaces its variant's legacy `validation-*` colours (in
 * `variantClassesExperimental`) with the reworked `semantic-*` /
 * `button-background-*` tiers. The matching entry is appended after the base
 * recipe (Tailwind-merge lets the override win) only when BOTH the forms and
 * buttons flags are on — see `use-button-props.ts`, the one place any button
 * recipe is resolved.
 *
 * Note: only success-toned variants change colour here; neutral variants
 * (default, secondary, outline, …) are unaffected and have no entry.
 */
export const buttonToneColourOverridesExperimental: Partial<
  Record<keyof typeof variantClassesExperimental, string>
> = {
  success:
    'bg-button-background-success text-text-primary-invert hover:bg-button-background-success-hover active:bg-button-background-success-active',
  success_ghost:
    'text-semantic-text-success hover:bg-semantic-background-success-hover active:bg-semantic-background-success-active active:text-semantic-text-success-active',
};

export const sizeClassesExperimental = {
  /** @deprecated Use `xs` instead as it fails WCAG 2.5.8. */
  xxs: cn(
    'h-6 gap-1 rounded-md px-2 py-1.5',
    'has-[[data-icon=inline-end]]:pe-1.5 has-[[data-icon=inline-start]]:ps-1.5'
  ),
  xs: cn(
    'h-7 gap-1 rounded-md px-2.5 py-2',
    'has-[[data-icon=inline-end]]:pe-2 has-[[data-icon=inline-start]]:ps-2'
  ),
  // 10px sits between `--radius-md` (8px) and `--radius-lg` (12px); the scale has
  // no step there, so it stays an arbitrary value rather than widening the shared
  // radius tokens for a single button size.
  sm: cn(
    'h-8 gap-1 rounded-[10px] px-2.5 py-[9px]',
    'has-[[data-icon=inline-end]]:pe-2 has-[[data-icon=inline-start]]:ps-2'
  ),
  md: cn(
    'h-9 gap-1.5 rounded-lg px-3 py-2.5',
    'has-[[data-icon=inline-end]]:pe-2.5 has-[[data-icon=inline-start]]:ps-2.5'
  ),
  lg: cn(
    'h-10 gap-1.5 rounded-lg px-3 py-[11px]',
    'has-[[data-icon=inline-end]]:pe-2.5 has-[[data-icon=inline-start]]:ps-2.5'
  ),
  /**
   * 44px — the iOS HIG and WCAG 2.5.5 minimum target size. Use for any control
   * a finger will hit: mobile surfaces, tablet-facing flows, device-pairing
   * screens. Applies to secondary actions in the same stack too — a ghost
   * "Cancel" needs the same target as the primary beside it.
   */
  touch: cn(
    'h-11 gap-2 rounded-lg px-4 py-3',
    'has-[[data-icon=inline-end]]:pe-3 has-[[data-icon=inline-start]]:ps-3'
  ),
} as const;

export const buttonVariantsExperimental = cva(
  cn(
    'group/button inline-flex shrink-0 cursor-pointer select-none items-center justify-center whitespace-nowrap text-sm font-medium outline-none transition-all duration-200',
    // Border gutter: every variant carries a 1px border, transparent unless the
    // variant recolours it. That gives every variant the same content box (so
    // auto-widths match across variants) and a permanently reserved 1px line for
    // the focus / `aria-invalid` border swaps to paint into — without it those
    // swaps are colour-only utilities over a 0px border and never render.
    //
    // Deliberately NOT paired with `bg-clip-padding` (DS-787 shipped the pair;
    // the clip was removed after measuring it). Clipping the fill to the padding
    // box made filled variants paint 34px inside their 36px box, so they
    // rendered *smaller* than the bordered variants beside them — a bordered
    // variant's painted extent includes its 1px border — and 2px short against
    // every other 36px control. Fills now paint to the border box, making them
    // pixel-identical to the bordered variants.
    'border border-transparent',
    'disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50',
    // Focus treatment: 1px opaque border-focus line + 1.5px border-focus/60
    // halo — one token, the halo fading out of the line.
    focusRingSolid,
    focusRingInvalidExperimental,
    '[&_svg]:pointer-events-none [&_svg]:shrink-0'
  ),
  {
    variants: {
      variant: variantClassesExperimental,
      size: sizeClassesExperimental,
      iconOnly: { true: '', false: '' },
      disableAutomaticTypographyClasses: { false: '', true: '' },
      disableAutomaticSvgSizing: { false: '', true: '' },
      disabled: { true: 'pointer-events-none', false: '' },
      flush: {
        all: '',
        horizontal: '',
        vertical: '',
        start: '',
        end: '',
        top: '',
        bottom: '',
      },
    },
    compoundVariants: [
      {
        iconOnly: true,
        size: 'xxs',
        className: 'size-6 p-0',
      },
      {
        iconOnly: true,
        size: 'xs',
        className: 'size-7 p-0',
      },
      {
        iconOnly: true,
        size: 'sm',
        className: 'size-8 p-0',
      },
      {
        iconOnly: true,
        size: 'md',
        className: 'size-9 p-0',
      },
      {
        iconOnly: true,
        size: 'lg',
        className: 'size-10 p-0',
      },
      {
        iconOnly: true,
        size: 'touch',
        className: 'size-11 p-0',
      },

      {
        disableAutomaticTypographyClasses: false,
        size: 'xxs',
        className: cn(
          typographyVariant({
            variant: 'captionBold',
            className: 'leading-6',
          })
        ),
      },
      {
        disableAutomaticTypographyClasses: false,
        size: 'xs',
        className: cn(
          typographyVariant({
            variant: 'captionBold',
            className: 'leading-7',
          })
        ),
      },

      {
        disableAutomaticTypographyClasses: false,
        size: 'sm',
        className: cn(
          typographyVariant({
            variant: 'p2Bold',
            className: 'leading-8',
          })
        ),
      },
      {
        disableAutomaticTypographyClasses: false,
        size: 'md',
        className: cn(
          typographyVariant({
            variant: 'p2Bold',
            className: 'leading-9',
          })
        ),
      },
      {
        disableAutomaticTypographyClasses: false,
        size: 'lg',
        className: cn(
          typographyVariant({
            variant: 'pBold',
            className: 'leading-10',
          })
        ),
      },
      {
        disableAutomaticTypographyClasses: false,
        size: 'touch',
        className: cn(
          typographyVariant({
            variant: 'h5',
            className: 'leading-11',
          })
        ),
      },

      // The `:not([class*='size-'])` guard lets callers override the icon size by
      // passing `className="size-X"` on the icon — the auto-size skips icons that
      // already have a `size-*` class.
      {
        disableAutomaticSvgSizing: false,
        size: 'xxs',
        className: cn("[&_svg:not([class*='size-'])]:size-3"),
      },
      {
        disableAutomaticSvgSizing: false,
        size: 'xs',
        className: cn("[&_svg:not([class*='size-'])]:size-3"),
      },
      {
        disableAutomaticSvgSizing: false,
        size: 'sm',
        className: cn("[&_svg:not([class*='size-'])]:size-3.5"),
      },
      {
        disableAutomaticSvgSizing: false,
        size: 'md',
        className: cn("[&_svg:not([class*='size-'])]:size-4"),
      },
      {
        disableAutomaticSvgSizing: false,
        size: 'lg',
        className: cn("[&_svg:not([class*='size-'])]:size-4.5"),
      },
      {
        disableAutomaticSvgSizing: false,
        size: 'touch',
        className: cn("[&_svg:not([class*='size-'])]:size-5"),
      },

      // The opt-out means "apply no size", not "the caller will size it" — so an
      // icon that sized itself neither by class nor by prop lands on lucide's
      // 24px default. `[width='24']` is the "caller expressed no size" signal,
      // since lucide always emits `width` and defaults it to 24. Only the
      // opt-out branch gets this guard: widening the `false` branch's would stop
      // auto-sizing overriding numeric props on every other button in the app.
      {
        disableAutomaticSvgSizing: true,
        size: 'xxs',
        className: cn("[&_svg[width='24']:not([class*='size-'])]:size-3"),
      },
      {
        disableAutomaticSvgSizing: true,
        size: 'xs',
        className: cn("[&_svg[width='24']:not([class*='size-'])]:size-3"),
      },
      {
        disableAutomaticSvgSizing: true,
        size: 'sm',
        className: cn("[&_svg[width='24']:not([class*='size-'])]:size-3.5"),
      },
      {
        disableAutomaticSvgSizing: true,
        size: 'md',
        className: cn("[&_svg[width='24']:not([class*='size-'])]:size-4"),
      },
      {
        disableAutomaticSvgSizing: true,
        size: 'lg',
        className: cn("[&_svg[width='24']:not([class*='size-'])]:size-4.5"),
      },
      {
        disableAutomaticSvgSizing: true,
        size: 'touch',
        className: cn("[&_svg[width='24']:not([class*='size-'])]:size-5"),
      },
      // Every variant fades uniformly when disabled via `disabled:opacity-50` on
      // the base recipe (the `<Button disabled>` path).
      {
        disabled: true,
        className: 'opacity-50',
      },

      // Flush compound variants — negative margins matching each size's px/py so the
      // button optically aligns with surrounding content. The values mirror the size
      // padding tokens above; update both together if size padding ever changes.
      {
        flush: 'all',
        variant: FLUSHABLE_VARIANTS,
        size: 'xxs',
        className: '-mx-1.5 -my-1',
      },
      {
        flush: 'all',
        variant: FLUSHABLE_VARIANTS,
        size: 'xs',
        className: '-mx-2.5 -my-2',
      },
      {
        flush: 'all',
        variant: FLUSHABLE_VARIANTS,
        size: 'sm',
        className: '-mx-2.5 [margin-block:-9px]',
      },
      {
        flush: 'all',
        variant: FLUSHABLE_VARIANTS,
        size: 'md',
        className: '-mx-3 -my-2.5',
      },
      {
        flush: 'all',
        variant: FLUSHABLE_VARIANTS,
        size: 'lg',
        className: '-mx-3 [margin-block:-11px]',
      },
      {
        flush: 'all',
        variant: FLUSHABLE_VARIANTS,
        size: 'touch',
        className: '-mx-4 -my-3',
      },

      {
        flush: 'horizontal',
        variant: FLUSHABLE_VARIANTS,
        size: 'xxs',
        className: '-mx-1.5',
      },
      {
        flush: 'horizontal',
        variant: FLUSHABLE_VARIANTS,
        size: 'xs',
        className: '-mx-2.5',
      },
      {
        flush: 'horizontal',
        variant: FLUSHABLE_VARIANTS,
        size: 'sm',
        className: '-mx-2.5',
      },
      {
        flush: 'horizontal',
        variant: FLUSHABLE_VARIANTS,
        size: 'md',
        className: '-mx-3',
      },
      {
        flush: 'horizontal',
        variant: FLUSHABLE_VARIANTS,
        size: 'lg',
        className: '-mx-3',
      },
      {
        flush: 'horizontal',
        variant: FLUSHABLE_VARIANTS,
        size: 'touch',
        className: '-mx-4',
      },

      {
        flush: 'vertical',
        variant: FLUSHABLE_VARIANTS,
        size: 'xxs',
        className: '-my-1',
      },
      {
        flush: 'vertical',
        variant: FLUSHABLE_VARIANTS,
        size: 'xs',
        className: '-my-2',
      },
      {
        flush: 'vertical',
        variant: FLUSHABLE_VARIANTS,
        size: 'sm',
        className: '[margin-block:-9px]',
      },
      {
        flush: 'vertical',
        variant: FLUSHABLE_VARIANTS,
        size: 'md',
        className: '-my-2.5',
      },
      {
        flush: 'vertical',
        variant: FLUSHABLE_VARIANTS,
        size: 'lg',
        className: '[margin-block:-11px]',
      },
      {
        flush: 'vertical',
        variant: FLUSHABLE_VARIANTS,
        size: 'touch',
        className: '-my-3',
      },

      {
        flush: 'start',
        variant: FLUSHABLE_VARIANTS,
        size: 'xxs',
        className: '-ms-1.5',
      },
      {
        flush: 'start',
        variant: FLUSHABLE_VARIANTS,
        size: 'xs',
        className: '-ms-2.5',
      },
      {
        flush: 'start',
        variant: FLUSHABLE_VARIANTS,
        size: 'sm',
        className: '-ms-2.5',
      },
      {
        flush: 'start',
        variant: FLUSHABLE_VARIANTS,
        size: 'md',
        className: '-ms-3',
      },
      {
        flush: 'start',
        variant: FLUSHABLE_VARIANTS,
        size: 'lg',
        className: '-ms-3',
      },
      {
        flush: 'start',
        variant: FLUSHABLE_VARIANTS,
        size: 'touch',
        className: '-ms-4',
      },

      {
        flush: 'end',
        variant: FLUSHABLE_VARIANTS,
        size: 'xxs',
        className: '-me-1.5',
      },
      {
        flush: 'end',
        variant: FLUSHABLE_VARIANTS,
        size: 'xs',
        className: '-me-2.5',
      },
      {
        flush: 'end',
        variant: FLUSHABLE_VARIANTS,
        size: 'sm',
        className: '-me-2.5',
      },
      {
        flush: 'end',
        variant: FLUSHABLE_VARIANTS,
        size: 'md',
        className: '-me-3',
      },
      {
        flush: 'end',
        variant: FLUSHABLE_VARIANTS,
        size: 'lg',
        className: '-me-3',
      },
      {
        flush: 'end',
        variant: FLUSHABLE_VARIANTS,
        size: 'touch',
        className: '-me-4',
      },

      {
        flush: 'top',
        variant: FLUSHABLE_VARIANTS,
        size: 'xxs',
        className: '-mt-1',
      },
      {
        flush: 'top',
        variant: FLUSHABLE_VARIANTS,
        size: 'xs',
        className: '-mt-2',
      },
      {
        flush: 'top',
        variant: FLUSHABLE_VARIANTS,
        size: 'sm',
        className: 'mt-[-9px]',
      },
      {
        flush: 'top',
        variant: FLUSHABLE_VARIANTS,
        size: 'md',
        className: '-mt-2.5',
      },
      {
        flush: 'top',
        variant: FLUSHABLE_VARIANTS,
        size: 'lg',
        className: 'mt-[-11px]',
      },
      {
        flush: 'top',
        variant: FLUSHABLE_VARIANTS,
        size: 'touch',
        className: '-mt-3',
      },

      {
        flush: 'bottom',
        variant: FLUSHABLE_VARIANTS,
        size: 'xxs',
        className: '-mb-1',
      },
      {
        flush: 'bottom',
        variant: FLUSHABLE_VARIANTS,
        size: 'xs',
        className: '-mb-2',
      },
      {
        flush: 'bottom',
        variant: FLUSHABLE_VARIANTS,
        size: 'sm',
        className: 'mb-[-9px]',
      },
      {
        flush: 'bottom',
        variant: FLUSHABLE_VARIANTS,
        size: 'md',
        className: '-mb-2.5',
      },
      {
        flush: 'bottom',
        variant: FLUSHABLE_VARIANTS,
        size: 'lg',
        className: 'mb-[-11px]',
      },
      {
        flush: 'bottom',
        variant: FLUSHABLE_VARIANTS,
        size: 'touch',
        className: '-mb-3',
      },
    ],
    defaultVariants: {
      variant: 'default',
      size: 'md',
      disableAutomaticTypographyClasses: false,
      disableAutomaticSvgSizing: false,
    },
  }
);
